import React, { useEffect, useMemo, useState } from 'react';
import {
  createArea,
  createSite,
  deleteArea,
  deleteSite,
  getSites,
  updateArea,
  updateSite,
} from '../services/api';
import { Area, Site, User } from '../types';

interface SitesProps {
  user: User;
}

type SiteModalState = { id?: string; name: string; description: string };
type AreaModalState = { id?: string; site_id: string; name: string; description: string };

export default function SitesPage({ user }: SitesProps) {
  const isAdmin = user.role === 'admin';
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [siteModal, setSiteModal] = useState<SiteModalState | null>(null);
  const [areaModal, setAreaModal] = useState<AreaModalState | null>(null);

  useEffect(() => {
    loadSites();
  }, []);

  async function loadSites() {
    try {
      const data = await getSites();
      setSites(data);
      setSelectedSiteId((current) =>
        current && data.some((site: Site) => site.id === current) ? current : data[0]?.id || ''
      );
    } catch {
      // Keep the last known hierarchy visible.
    }
  }

  async function saveSite() {
    if (!siteModal || !siteModal.name.trim()) return;
    setLoading(true);
    try {
      if (siteModal.id) {
        await updateSite(siteModal.id, { name: siteModal.name, description: siteModal.description });
      } else {
        await createSite({ name: siteModal.name, description: siteModal.description });
      }
      setSiteModal(null);
      await loadSites();
    } catch {
      window.alert('Failed to save site');
    } finally {
      setLoading(false);
    }
  }

  async function saveArea() {
    if (!areaModal || !areaModal.name.trim() || !areaModal.site_id) return;
    setLoading(true);
    try {
      if (areaModal.id) {
        await updateArea(areaModal.id, {
          name: areaModal.name,
          description: areaModal.description,
          site_id: areaModal.site_id,
        });
      } else {
        await createArea({
          site_id: areaModal.site_id,
          name: areaModal.name,
          description: areaModal.description,
        });
      }
      setAreaModal(null);
      await loadSites();
    } catch {
      window.alert('Failed to save area');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteSite(site: Site) {
    const deviceCount = getSiteDeviceCount(site);
    const warning = deviceCount > 0 ? ` Its ${deviceCount} device(s) will become unassigned.` : '';
    if (!window.confirm(`Delete site "${site.name}" and all its areas?${warning}`)) return;
    setLoading(true);
    try {
      await deleteSite(site.id);
      await loadSites();
    } catch {
      window.alert('Failed to delete site');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteArea(area: Area) {
    const warning = (area.device_count || 0) > 0 ? ` Its ${area.device_count} device(s) will become unassigned.` : '';
    if (!window.confirm(`Delete area "${area.name}"?${warning}`)) return;
    setLoading(true);
    try {
      await deleteArea(area.id);
      await loadSites();
    } catch {
      window.alert('Failed to delete area');
    } finally {
      setLoading(false);
    }
  }

  const totals = useMemo(() => {
    const totalAreas = sites.reduce((sum, site) => sum + site.areas.length, 0);
    const totalDevices = sites.reduce((sum, site) => sum + getSiteDeviceCount(site), 0);
    const emptyAreas = sites.reduce(
      (sum, site) => sum + site.areas.filter((area) => (area.device_count || 0) === 0).length,
      0
    );
    return { totalAreas, totalDevices, emptyAreas };
  }, [sites]);

  const selectedSite = selectedSiteId
    ? sites.find((site) => site.id === selectedSiteId) || null
    : sites[0] || null;

  return (
    <div className="sd-sites-page">
      <section className="sd-page-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">MANAGE</div>
          <h1>Sites & Areas</h1>
          <p>Group devices by site, area, floor-plan scope, and operational ownership.</p>
        </div>
        {isAdmin && (
          <div className="sd-page-actions">
            <button className="sd-primary-button" disabled={loading} onClick={() => setSiteModal({ name: '', description: '' })} type="button">
              New site
            </button>
          </div>
        )}
      </section>

      <section className="sd-map-stat-grid">
        <HierarchyStat label="Sites" value={sites.length} detail="Tenant locations" tone="blue" />
        <HierarchyStat label="Areas" value={totals.totalAreas} detail="Operational zones" tone="success" />
        <HierarchyStat label="Devices" value={totals.totalDevices} detail="Assigned assets" tone="warning" />
        <HierarchyStat label="Empty areas" value={totals.emptyAreas} detail="Need device assignment" tone="danger" />
      </section>

      {sites.length === 0 ? (
        <article className="sd-panel sd-sites-empty">
          <h2>No sites configured</h2>
          <p>{isAdmin ? 'Create a site to start grouping devices by location.' : 'Ask an administrator to create sites.'}</p>
          {isAdmin && (
            <button className="sd-primary-button" onClick={() => setSiteModal({ name: '', description: '' })} type="button">
              Create first site
            </button>
          )}
        </article>
      ) : (
        <section className="sd-sites-workspace">
          <article className="sd-panel sd-site-hierarchy-panel">
            <div className="sd-panel-header sd-sites-toolbar">
              <div>
                <h2>Site hierarchy</h2>
                <p>{sites.length} sites across {totals.totalAreas} areas</p>
              </div>
            </div>

            <div className="sd-site-hierarchy-list">
              {sites.map((site) => (
                <button
                  className={`sd-site-card ${selectedSite?.id === site.id ? 'is-selected' : ''}`}
                  key={site.id}
                  onClick={() => setSelectedSiteId(site.id)}
                  type="button"
                >
                  <div className="sd-site-card-header">
                    <div>
                      <strong>{site.name}</strong>
                      <span>{site.description || 'No description'}</span>
                    </div>
                    <b>{getSiteDeviceCount(site)}</b>
                  </div>

                  <div className="sd-area-stack">
                    {site.areas.length === 0 && <span className="sd-area-empty">No areas yet</span>}
                    {site.areas.map((area) => (
                      <div className="sd-area-row" key={area.id}>
                        <div>
                          <strong>{area.name}</strong>
                          <span>{area.description || 'No description'}</span>
                        </div>
                        <em>{area.device_count || 0} devices</em>
                      </div>
                    ))}
                  </div>

                  {isAdmin && (
                    <div className="sd-site-card-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAreaModal({ site_id: site.id, name: '', description: '' });
                        }}
                        type="button"
                      >
                        Add area
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSiteModal({ id: site.id, name: site.name, description: site.description || '' });
                        }}
                        type="button"
                      >
                        Edit site
                      </button>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </article>

          <SiteInspector
            handleDeleteArea={handleDeleteArea}
            handleDeleteSite={handleDeleteSite}
            isAdmin={isAdmin}
            loading={loading}
            setAreaModal={setAreaModal}
            setSiteModal={setSiteModal}
            site={selectedSite}
          />
        </section>
      )}

      {siteModal && (
        <SiteModal
          loading={loading}
          saveSite={saveSite}
          setSiteModal={setSiteModal}
          siteModal={siteModal}
        />
      )}

      {areaModal && (
        <AreaModal
          areaModal={areaModal}
          loading={loading}
          saveArea={saveArea}
          setAreaModal={setAreaModal}
          sites={sites}
        />
      )}
    </div>
  );
}

function SiteInspector({
  handleDeleteArea,
  handleDeleteSite,
  isAdmin,
  loading,
  setAreaModal,
  setSiteModal,
  site,
}: {
  handleDeleteArea: (area: Area) => void;
  handleDeleteSite: (site: Site) => void;
  isAdmin: boolean;
  loading: boolean;
  setAreaModal: React.Dispatch<React.SetStateAction<AreaModalState | null>>;
  setSiteModal: React.Dispatch<React.SetStateAction<SiteModalState | null>>;
  site: Site | null;
}) {
  if (!site) {
    return (
      <aside className="sd-panel sd-site-inspector">
        <div className="sd-panel-header">
          <div>
            <h2>Site inspector</h2>
            <p>Select a site to inspect areas.</p>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sd-panel sd-site-inspector">
      <div className="sd-panel-header">
        <div>
          <h2>Site inspector</h2>
          <p>Area and device assignment summary</p>
        </div>
      </div>
      <div className="sd-site-inspector-body">
        <div className="sd-site-inspector-title">
          <span>{site.name.slice(0, 1).toUpperCase()}</span>
          <div>
            <h3>{site.name}</h3>
            <p>{site.description || 'No site description'}</p>
          </div>
        </div>

        <div className="sd-inspector-facts">
          <FactRow label="Areas" value={String(site.areas.length)} />
          <FactRow label="Devices" value={String(getSiteDeviceCount(site))} />
          <FactRow label="Created" value={site.created_at ? new Date(site.created_at).toLocaleDateString() : 'Unknown'} />
        </div>

        <div className="sd-inspector-section-title">Areas</div>
        <div className="sd-site-area-list">
          {site.areas.length === 0 && <div className="sd-empty-row">No areas configured for this site.</div>}
          {site.areas.map((area) => (
            <div className="sd-site-area-item" key={area.id}>
              <div>
                <strong>{area.name}</strong>
                <span>{area.description || 'No description'}</span>
              </div>
              <em>{area.device_count || 0}</em>
              {isAdmin && (
                <div className="sd-site-area-actions">
                  <button
                    onClick={() =>
                      setAreaModal({
                        id: area.id,
                        site_id: area.site_id,
                        name: area.name,
                        description: area.description || '',
                      })
                    }
                    type="button"
                  >
                    Edit
                  </button>
                  <button onClick={() => handleDeleteArea(area)} type="button">
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="sd-device-quick-actions">
            <button onClick={() => setAreaModal({ site_id: site.id, name: '', description: '' })} disabled={loading} type="button">
              Add area
            </button>
            <button
              onClick={() => setSiteModal({ id: site.id, name: site.name, description: site.description || '' })}
              disabled={loading}
              type="button"
            >
              Edit site
            </button>
            <button className="is-danger" onClick={() => handleDeleteSite(site)} disabled={loading} type="button">
              Delete site
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function SiteModal({
  loading,
  saveSite,
  setSiteModal,
  siteModal,
}: {
  loading: boolean;
  saveSite: () => void;
  setSiteModal: React.Dispatch<React.SetStateAction<SiteModalState | null>>;
  siteModal: SiteModalState;
}) {
  return (
    <div className="sd-drawer-overlay sd-modal-overlay" onClick={() => setSiteModal(null)}>
      <section className="sd-site-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-edit-drawer-header">
          <div>
            <span>{siteModal.id ? 'Edit site' : 'New site'}</span>
            <h2>{siteModal.id ? siteModal.name : 'Create site'}</h2>
          </div>
          <button onClick={() => setSiteModal(null)} type="button">Close</button>
        </div>
        <div className="sd-form-grid is-single">
          <Field label="Name" hint="Facility or customer site name shown in filters, dashboards, and reports.">
            <input
              autoFocus
              placeholder="e.g. Port Harcourt Terminal"
              value={siteModal.name}
              onChange={(e) => setSiteModal({ ...siteModal, name: e.target.value })}
            />
          </Field>
          <Field label="Description" hint="Optional short context for operators, such as industry, process, or location detail.">
            <input
              placeholder="e.g. Main oil and gas operations site"
              value={siteModal.description}
              onChange={(e) => setSiteModal({ ...siteModal, description: e.target.value })}
            />
          </Field>
        </div>
        <div className="sd-drawer-actions">
          <button onClick={() => setSiteModal(null)} type="button">Cancel</button>
          <button disabled={loading || !siteModal.name.trim()} onClick={saveSite} type="button">
            {loading ? 'Saving...' : siteModal.id ? 'Save changes' : 'Create site'}
          </button>
        </div>
      </section>
    </div>
  );
}

function AreaModal({
  areaModal,
  loading,
  saveArea,
  setAreaModal,
  sites,
}: {
  areaModal: AreaModalState;
  loading: boolean;
  saveArea: () => void;
  setAreaModal: React.Dispatch<React.SetStateAction<AreaModalState | null>>;
  sites: Site[];
}) {
  return (
    <div className="sd-drawer-overlay sd-modal-overlay" onClick={() => setAreaModal(null)}>
      <section className="sd-site-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-edit-drawer-header">
          <div>
            <span>{areaModal.id ? 'Edit area' : 'New area'}</span>
            <h2>{areaModal.id ? areaModal.name : 'Create area'}</h2>
          </div>
          <button onClick={() => setAreaModal(null)} type="button">Close</button>
        </div>
        <div className="sd-form-grid is-single">
          <Field label="Site" hint="Parent site that contains this operational area.">
            <select value={areaModal.site_id} onChange={(e) => setAreaModal({ ...areaModal, site_id: e.target.value })}>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Name" hint="Operational zone, floor, room, process area, or equipment yard name.">
            <input
              autoFocus
              placeholder="e.g. Compressor Hall"
              value={areaModal.name}
              onChange={(e) => setAreaModal({ ...areaModal, name: e.target.value })}
            />
          </Field>
          <Field label="Description" hint="Optional note that explains what happens in this area.">
            <input
              placeholder="e.g. Main process floor"
              value={areaModal.description}
              onChange={(e) => setAreaModal({ ...areaModal, description: e.target.value })}
            />
          </Field>
        </div>
        <div className="sd-drawer-actions">
          <button onClick={() => setAreaModal(null)} type="button">Cancel</button>
          <button disabled={loading || !areaModal.name.trim() || !areaModal.site_id} onClick={saveArea} type="button">
            {loading ? 'Saving...' : areaModal.id ? 'Save changes' : 'Create area'}
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ children, hint, label }: { children: React.ReactNode; hint?: string; label: string }) {
  return (
    <label className="sd-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function HierarchyStat({
  detail,
  label,
  tone,
  value,
}: {
  detail: string;
  label: string;
  tone: 'blue' | 'danger' | 'success' | 'warning';
  value: number;
}) {
  return (
    <article className={`sd-map-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="sd-fact-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function getSiteDeviceCount(site: Site) {
  return site.areas.reduce((sum, area) => sum + (area.device_count || 0), 0);
}
